import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { resolveItemModelId } from '@sorrem/shared';
import type { UIInventoryItem } from '@sorrem-client/types';
import type { ItemFrameSource, ItemFrameViewport, ItemFramesCommand } from '@sorrem-client/game/render-config';
import { findItemEffectLook } from '@sorrem-client/game/item-effect-look';
import { toItemFrameModelKey } from '@sorrem-client/game/item-frames/item-frame-key';
import { Icon, ItemIcon } from '@sorrem-client/ui/components';
import { useDisplaySettings } from '@sorrem-client/ui/hooks';
import { useRenderWorkerSession } from '@sorrem-client/ui/hooks/use-render-worker-session';
import { useCharacterHomeCharacter } from '@sorrem-client/ui/features/CharacterHome/components/CharacterHomeItemTransfer';
import ItemFramesRenderWorker from './item-frames-render-worker-entry?worker';
import styles from './ItemFrames.module.scss';

/** Dragging this far turns the items all the way around. */
const PIXELS_PER_TURN = 600;

export interface ItemFrames {
    /** The one transparent canvas that draws every item: placed over the cards, inside a positioned container. */
    readonly overlay: ReactNode;
    /** What goes in the art box of the card for `items[index]`: the frame its model is drawn into. */
    readonly artFor: (index: number) => ReactNode;
    /** Swings every item once, so its swing effects show (only while the frames animate). */
    readonly swing: () => void;
}

/** What a frame draws for an item: armor on the character's body (it has no model of its own), else its own model with its effect. */
function toFrameSource(item: UIInventoryItem, characterModelId: string): ItemFrameSource {
    return item.equipmentType === 'armor'
        ? { kind: 'worn', characterModelId, itemId: resolveItemModelId(item) }
        : { kind: 'item', modelId: resolveItemModelId(item), look: findItemEffectLook(item.instance) ?? null };
}

/**
 * Item cards' items in 3D (#287: the workbench's card, the Named Item
 * Designer's), each drawn in its own card's art box, with its rarity and
 * upgrade effect; dragging one turns them all the same way. One transparent canvas over the cards draws them all
 * (`item-frames-render-worker.ts`), each into its art box's place; an art
 * box shows the item's icon, with a loading icon, until its model is in
 * (just the icon if the model fails). Armor is worn on `wearerModelId`, or
 * else the Character Home character's body.
 */
export function useItemFrames(items: readonly UIInventoryItem[], wearerModelId?: string): ItemFrames {
    const { characterHomeEffects } = useDisplaySettings();
    // The item frames are 3D: they turn only at "All".
    const animates = characterHomeEffects === 'all';
    const animationsRef = useRef(animates);
    animationsRef.current = animates;
    const frameElements = useRef<Array<HTMLDivElement | null>>([]);
    const homeCharacterModelId = useCharacterHomeCharacter()?.characterModelId;
    const characterModelId = wearerModelId ?? homeCharacterModelId ?? '';
    const sources = items.map((item) => toFrameSource(item, characterModelId));
    // Each frame's model as the worker keys it, so its loaded message can be matched to what is asked for now; a new look alone loads nothing.
    const frameKeys = sources.map(toItemFrameModelKey);
    const sourcesKey = JSON.stringify(sources);
    // What each frame has finished loading, by index: until it is the current source, its art box keeps the icon.
    const [loaded, setLoaded] = useState<Readonly<Record<number, { readonly key: string; readonly ok: boolean }>>>({});

    const { containerRef, sessionRef, sessionGeneration } = useRenderWorkerSession<ItemFramesCommand>({
        worker: ItemFramesRenderWorker,
        callbacks: {
            onItemFrameLoaded: ({ index, key, ok }) => {
                setLoaded((current) => ({ ...current, [index]: { key, ok } }));
                // A still frame (animations off) paints once its items are in.
                if (!animationsRef.current) sessionRef.current?.sendCommand({ type: 'render-frame' });
            },
        },
        onSessionCreated: (session) => {
            session.sendCommand({ type: 'set-continuous-rendering', enabled: animationsRef.current });
        },
    });

    // Each effect that sends the worker its state runs again for a fresh session after a lost GPU device (#344).
    useEffect(() => {
        sessionRef.current?.sendCommand({ type: 'load-item-frames', frames: JSON.parse(sourcesKey) as ItemFrameSource[] });
    }, [sessionRef, sessionGeneration, sourcesKey]);

    useEffect(() => {
        sessionRef.current?.sendCommand({ type: 'set-continuous-rendering', enabled: animates });
        if (!animates) sessionRef.current?.sendCommand({ type: 'render-frame' });
    }, [sessionRef, sessionGeneration, animates]);

    // Each art box's place, as a share of the canvas from its bottom left, so each item draws inside its own box.
    const sendViewports = useCallback((): void => {
        const area = containerRef.current?.getBoundingClientRect();
        if (!area || area.width <= 0 || area.height <= 0) return;
        const viewports: ItemFrameViewport[] = frameElements.current.slice(0, items.length).map((frame) => {
            const box = frame?.getBoundingClientRect();
            if (!box) return { x: 0, y: 0, width: 0, height: 0 };
            return {
                x: (box.left - area.left) / area.width,
                y: (area.bottom - box.bottom) / area.height,
                width: box.width / area.width,
                height: box.height / area.height,
            };
        });
        sessionRef.current?.sendCommand({ type: 'set-item-frame-viewports', viewports });
        if (!animationsRef.current) sessionRef.current?.sendCommand({ type: 'render-frame' });
    }, [containerRef, sessionRef, items.length]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        sendViewports();
        const observer = new ResizeObserver(sendViewports);
        observer.observe(container);
        for (const frame of frameElements.current) {
            if (frame) observer.observe(frame);
        }
        return (): void => observer.disconnect();
    }, [containerRef, sessionGeneration, sendViewports]);

    // Dragging on any art box turns every item together; the canvas itself lets the pointer through.
    const dragRef = useRef<{ readonly pointerId: number; lastX: number } | null>(null);
    useEffect(() => {
        const onPointerMove = (event: PointerEvent): void => {
            const drag = dragRef.current;
            if (drag === null || event.pointerId !== drag.pointerId) return;
            const deltaX = event.clientX - drag.lastX;
            drag.lastX = event.clientX;
            if (deltaX === 0) return;
            // Negative: dragging right swings the item's near side to the right, as if turning it in your hand.
            sessionRef.current?.sendCommand({ type: 'turn-item-frames', radians: -(deltaX / PIXELS_PER_TURN) * Math.PI * 2 });
            if (!animationsRef.current) sessionRef.current?.sendCommand({ type: 'render-frame' });
        };
        const onPointerUp = (event: PointerEvent): void => {
            if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
        };
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);
        return (): void => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('pointercancel', onPointerUp);
        };
    }, [sessionRef]);

    const artFor = (index: number): ReactNode => {
        const item = items[index];
        const done = loaded[index]?.key === frameKeys[index] ? loaded[index] : undefined;
        return (
            <div
                ref={(element): void => { frameElements.current[index] = element; }}
                className={styles.frame}
                onPointerDown={(event): void => {
                    if (event.button !== 0) return;
                    dragRef.current = { pointerId: event.pointerId, lastX: event.clientX };
                    event.preventDefault();
                }}
            >
                {done?.ok !== true && item !== undefined && <ItemIcon modelId={resolveItemModelId(item)} />}
                {done === undefined && <Icon icon='loading' spin='fast' className={styles.spinner} tooltip='Loading' />}
            </div>
        );
    };

    const swing = useCallback((): void => {
        sessionRef.current?.sendCommand({ type: 'swing-item-frames' });
    }, [sessionRef]);

    return {
        overlay: <div ref={containerRef} className={styles.overlay} aria-hidden='true' />,
        artFor,
        swing,
    };
}
